import type { ReactNode } from 'react';
import { useEffect, useMemo, useState } from 'react';
import ChartWrapper from '@/pages/CompanyReport/components/Overview/components/ChartWrapper';
import MultipleVsPeersTable from './components/MultipleVsPeersTable';
import MultipleVsPeersChart from './components/MultipleVsPeersChart';
import { COMPANY_VALUATION_MULTIPLE_VS_PEERS } from '@/constants/feedbackSections';
import type { PreferredMultiple, UserPeerSelection } from '../../selector';
import { useValuationData } from '../../selector';
import { Toggle, Text, Alert, AlertAction } from '@simplywallst/ui-core';
import EditSolid from '@simplywallst/ui-core/icons/EditSolid';
import {
  MultipleVsPeersWrapper,
  ToggleWrapper,
  ToggleLabel,
  DropdownWrapper,
  PillButton,
} from './styled';
import { getCustomPeersData, usePeers } from '../../query';
import type { CustomPeers } from '../../query';

import type { CurrencyISO } from '@/constants/currencies';
import { mapCompanyToPeer } from '../../utils';
import Tooltip from '@/components/Tooltip';
import ReportSubSection from '@components/ReportSubSection';
import MultipleDropdown from '../MultipleDropdown';
import CustomPeersModal from './components/MultipleVsPeersChart/components/CustomPeersModal';
import { isEqual } from 'lodash';
import { useTracking } from 'react-tracking';
import {
  useDeleteUserCompanyPeers,
  useUpsertUserCompanyPeers,
  useUserCompanyPeers,
} from '@/hooks/useUserCompanyPeers/useUserCompanyPeers';
import SavedPeersDropdown from '@/pages/CompanyReport/components/Valuation/components/SavedPeersDropdown';
import PeersHelpContent from '@/pages/CompanyReport/components/Valuation/components/MultipleVsPeers/components/PeersHelpContent';
import { AlertWrapper } from '@components/ReportSubSection/styled';
import { useVisibleSectionsIsPristine } from '@/pages/CompanyReport/components/VisibleSectionsContextProvider';
import { getPeersRatioAverage } from './utils';
import { useValuationTranslation } from '@/pages/CompanyReport/components/Valuation/hooks/useValuationTranslation';
import { useBaseMetricLabel } from './hooks';
import { useMetricFullLabel } from '../../hooks/useLabel';
import loadable from '@loadable/component';
import { useLearningContent } from '@/pages/CompanyReport/hooks/useLearningContent';

const LearningTile = loadable(
  () => import('@/components/LearningHubActionSheet'),
  {
    resolveComponent: (c) => c.LearningTile,
  }
);

type OptionalTooltipProps = {
  title: string;
  show: boolean;
  children: React.ReactNode;
};

const OptionalTooltip = ({ title, show, children }: OptionalTooltipProps) => {
  if (show) {
    return (
      <Tooltip openDelay={300} light title={<span>{title}</span>}>
        <>{children}</>
      </Tooltip>
    );
  }

  return <>{children}</>;
};

export interface Peer {
  companyId: string;
  name: string;
  tickerSymbol: string;
  uniqueSymbol: string;
  canonicalURL: string;
  listingCurrencyISO: CurrencyISO;
  ratio: number;
  ratioFormatted: string;
  earningsGrowth: number;
  earningsGrowthFormatted: string;
  salesGrowth: number;
  salesGrowthFormatted: string;
  marketCap: number;
  marketCapFormatted: string;
  marketCapUSD: number;
  marketCapUSDFormatted: string;
  preferredCurrentRatio?: number;
  currentRatio?: number;
  forwardRatio?: number;
}

interface Props {
  preferredMultiple: PreferredMultiple;
  statements?: ReactNode;
}

export const initCustomPeers = [
  { id: '', name: '' },
  { id: '', name: '' },
  { id: '', name: '' },
  { id: '', name: '' },
];

const MultipleVsPeers = ({ preferredMultiple, statements }: Props) => {
  const isPristine = useVisibleSectionsIsPristine();
  const { Track, trackEvent } = useTracking({ subject: 'MultipleVsPeers' });
  const [multiple, setMultiple] =
    useState<PreferredMultiple>(preferredMultiple);
  const [userPeerOption, setUserPeerOption] =
    useState<UserPeerSelection>('saved');
  const [toggleForward, setToggleForward] = useState(false);
  const {
    companyId,
    forwardPE,
    forwardPS,
    tickerSymbol,
    canonicalUrl,
    metricsData,
    uniqueSymbol,
  } = useValuationData();

  const [showEditModal, setShowEditModal] = useState(false);
  const [userPeerCollectionId, setUserPeerCollectionId] = useState<
    string | null
  >(null);
  const [savedUserPeers, setSavedUserPeers] = useState<CustomPeers>([]);
  const [deletionWasFinished, setDeletionWasFinished] =
    useState<boolean>(false);
  const [loadDefaultPeers, setLoadDefaultPeers] = useState<boolean>(false);
  const [customPeers, setCustomPeers] = useState<CustomPeers>(initCustomPeers);
  const [isCustomPeersSelected, setIsCustomPeersSelected] = useState(false);

  const { data, status: peersStatus } = usePeers({
    canonicalUrl,
    peers: isEqual(customPeers, initCustomPeers)
      ? []
      : [
          customPeers[0]?.id,
          customPeers[1]?.id,
          customPeers[2]?.id,
          customPeers[3]?.id,
        ].filter((id): id is string => typeof id === 'string'),
  });

  const savedCustomPeers = useMemo(() => {
    return data && getCustomPeersData(data.Company);
  }, [data]);

  const peers = useMemo((): Peer[] => {
    try {
      if (!data) return [];
      const { peers, ...companyData } = data.Company;
      const selectedCompany = mapCompanyToPeer(
        multiple,
        toggleForward,
        preferredMultiple
      )(companyData);
      const peersData: Peer[] = peers
        .map(({ company }) => company)
        .filter(
          (n): n is NonNullable<(typeof peers)[number]['company']> => n !== null
        )
        .map((company) =>
          mapCompanyToPeer(multiple, toggleForward, preferredMultiple)(company)
        );
      return peersData.concat(selectedCompany);
    } catch (error) {
      console.log(error);
      return [];
    }
  }, [data, multiple, preferredMultiple, toggleForward]);
  const noPeersData = peers.length === 1;

  const { average, averageFormatted } = getPeersRatioAverage(
    peers,
    data?.Company.uniqueSymbol
  );

  const defaultPeers = useMemo(() => {
    const peers: Peer[] = [70, 55, 28, 23, 17].map((value, i) => ({
      canonicalURL: '',
      companyId: i === 1 ? companyId : String(i),
      earningsGrowth: 0,
      earningsGrowthFormatted: '',
      listingCurrencyISO: 'USD',
      marketCap: 0,
      marketCapFormatted: 'n/a',
      marketCapUSD: 0,
      marketCapUSDFormatted: 'n/a',
      name: '',
      ratio: value,
      ratioFormatted: '',
      salesGrowth: 0,
      salesGrowthFormatted: '',
      tickerSymbol: '',
      uniqueSymbol: '',
    }));
    return peers;
  }, [companyId]);

  useEffect(() => {
    setToggleForward(false);
  }, [multiple]);

  //Initially set multiple selection to the company's preferred one
  useEffect(() => {
    setMultiple(preferredMultiple);
  }, [preferredMultiple]);

  const disableToggle =
    (multiple === 'PE' && forwardPE === 'n/a') ||
    (multiple === 'PS' && forwardPS === 'n/a');

  const savedPeers = useUserCompanyPeers(companyId);

  const t = useValuationTranslation();

  const { createUserPeers, creationStatus } =
    useUpsertUserCompanyPeers(companyId);

  const { deleteUserPeers, deletionStatus } =
    useDeleteUserCompanyPeers(companyId);

  useEffect(() => {
    if (savedPeers.status === 'success') {
      if (savedPeers.data.length > 0) {
        setUserPeerCollectionId(savedPeers.data[0].id);
        const customSavedPeers = savedPeers.data[0].peers.map((peer) => {
          return { id: peer.uniqueSymbol, name: '' };
        });
        if (
          !customPeers
            .map((item) => item.id)
            .filter((id): id is string => typeof id === 'string')
            .every((i) =>
              customSavedPeers.map((item) => item.id).includes(i)
            ) &&
          loadDefaultPeers === false
        ) {
          setCustomPeers([...customSavedPeers]);
          setIsCustomPeersSelected(true);
          setUserPeerOption('saved');
        }
      }
    }
  }, [savedPeers, loadDefaultPeers, customPeers]);

  useEffect(() => {
    //holding the actual saved peers in a state
    if (
      !isEqual(customPeers, initCustomPeers) ||
      //initial state
      (savedUserPeers.length === 0 &&
        peersStatus === 'success' &&
        !loadDefaultPeers)
    ) {
      setSavedUserPeers(savedCustomPeers ? savedCustomPeers : initCustomPeers);
    }
  }, [customPeers, data, peersStatus, savedUserPeers, loadDefaultPeers]);

  useEffect(() => {
    if (
      deletionStatus === 'success' &&
      userPeerCollectionId &&
      !deletionWasFinished
    ) {
      setUserPeerCollectionId(null);
      setSavedUserPeers(savedCustomPeers ? savedCustomPeers : initCustomPeers);
      setDeletionWasFinished(true);
    }
  }, [deletionStatus, userPeerCollectionId, deletionWasFinished]);

  const handleSubmitCustomPeers = (selectedPeers: CustomPeers) => {
    if (!isCustomPeersSelected) {
      setCustomPeers([...initCustomPeers]);
      setShowEditModal(false);
      if (userPeerCollectionId) {
        setDeletionWasFinished(false);
        deleteUserPeers(userPeerCollectionId);
      }
    } else {
      setCustomPeers([...selectedPeers]);
      setShowEditModal(false);

      const selectedPeersIds = selectedPeers.map((peer) => peer.id);

      createUserPeers({
        userPeerCollection: {
          name: 'Custom peers for ' + uniqueSymbol,
          uniqueSymbol: uniqueSymbol,
          description: 'Saved Peers Description',
          peers: selectedPeersIds
            .filter((id): id is string => typeof id === 'string')
            .map((id) => {
              return { uniqueSymbol: id };
            }),
        },
        collectionId: userPeerCollectionId,
      });
      setLoadDefaultPeers(false);
      setUserPeerOption('saved');

      trackEvent({
        action: 'submit',
        modifier: 'Edit Peers',
        uniqueSymbol: uniqueSymbol,
        customPeers: JSON.stringify(selectedPeers),
      });
    }
  };

  const handleCloseModal = () => {
    setShowEditModal(false);
    if (userPeerOption === 'edit' && loadDefaultPeers) {
      setUserPeerOption('default');
    } else {
      setUserPeerOption('saved');
    }
  };

  const handleResetAllPeers = (resetChart = false) => {
    if (resetChart) {
      setCustomPeers([...initCustomPeers]);
    }
    setLoadDefaultPeers(true);
  };

  const onDropDownSelection = (option: UserPeerSelection) => {
    if (option === 'edit') {
      setShowEditModal(true);
    }
    if (option === 'saved') {
      setLoadDefaultPeers(false);
      setIsCustomPeersSelected(true);
    }
    if (option === 'default') {
      handleResetAllPeers(true);
    }
    setUserPeerOption(option);
    trackEvent({
      action: 'select ' + option,
      modifier: 'Editing saved Peers ',
      uniqueSymbol,
    });
  };

  const getMetricLabel = useMetricFullLabel();
  const getBaseMetricLabel = useBaseMetricLabel();
  const { isCardDismissed, handleDismiss, contentKey } = useLearningContent(
    'valuation-stock-vs-peers'
  );

  const userSelectedMultiple = multiple || preferredMultiple;

  return (
    <Track>
      <ReportSubSection
        titleElement={isPristine ? 'h2' : 'h3'}
        title={t('vsPeers.heading', {
          metric: getMetricLabel(userSelectedMultiple),
        })}
        description={t('vsPeers.description', {
          tickerSymbol,
          metric: preferredMultiple,
        })}
        bodyRender={() => (
          <>
            <MultipleVsPeersWrapper>
              {creationStatus === 'error' && (
                <AlertWrapper>
                  <Alert
                    styleType="warning"
                    actions={[
                      <AlertAction key="ok">
                        {t('vsPeers.customPeers.error.close')}
                      </AlertAction>,
                    ]}
                  >
                    {t('vsPeers.customPeers.error.message')}
                  </Alert>
                </AlertWrapper>
              )}
              <DropdownWrapper>
                {(userPeerCollectionId === null ||
                  savedPeers.status === 'error') && (
                  <PillButton
                    styleType="passive"
                    alignIcon="left"
                    marginRight
                    onClick={() => {
                      setShowEditModal(true);
                      trackEvent({
                        action: 'click',
                        modifier: 'Edit Peers',
                        uniqueSymbol: data?.Company.uniqueSymbol,
                      });
                    }}
                    disabled={
                      !savedCustomPeers ||
                      peersStatus !== 'success' ||
                      deletionStatus === 'pending'
                    }
                  >
                    <EditSolid width="24" height="24" />
                    <Text typography="tiny">
                      {t('vsPeers.customPeers.edit')}
                    </Text>
                  </PillButton>
                )}
                {userPeerCollectionId && (
                  <SavedPeersDropdown
                    selectedOption={userPeerOption}
                    disabled={
                      !savedCustomPeers ||
                      peersStatus !== 'success' ||
                      deletionStatus === 'pending'
                    }
                    onSelect={onDropDownSelection}
                    id={'vsPeers'}
                  />
                )}
                {showEditModal && data && (
                  <CustomPeersModal
                    uniqueSymbol={data?.Company.uniqueSymbol}
                    currentPeers={savedUserPeers}
                    canonicalUrl={canonicalUrl}
                    onClose={handleCloseModal}
                    onResetAllPeers={() => handleResetAllPeers(false)}
                    onSubmitCustomPeers={handleSubmitCustomPeers}
                    onUpdatePeers={(isCustomPeerSelected) =>
                      setIsCustomPeersSelected(isCustomPeerSelected)
                    }
                  />
                )}
                <MultipleDropdown
                  id="vsPeers"
                  preferredMultiple={preferredMultiple}
                  multiple={multiple}
                  onSelect={setMultiple}
                />
              </DropdownWrapper>
              <ChartWrapper
                id="valuation-multiple-vs-peers"
                defaultModeText={
                  noPeersData ? t('vsPeers.noPeersData') : undefined
                }
                chart={
                  noPeersData ? (
                    <MultipleVsPeersChart
                      companyId={companyId}
                      preferredMultiple={preferredMultiple}
                      peers={defaultPeers}
                      peersAverage={38}
                      insufficientData={noPeersData}
                    />
                  ) : (
                    <MultipleVsPeersChart
                      companyId={companyId}
                      preferredMultiple={preferredMultiple}
                      peers={peers}
                      peersAverage={average}
                      status={peersStatus}
                    />
                  )
                }
                table={
                  <MultipleVsPeersTable
                    companyId={companyId}
                    tickerSymbol={tickerSymbol}
                    preferredMultiple={preferredMultiple}
                    peers={peers}
                    averageFormatted={averageFormatted}
                    useForwardRatio={toggleForward}
                  />
                }
                preRenderHeight={382}
                actionsMarginTop={16}
                feedbackLabel={COMPANY_VALUATION_MULTIPLE_VS_PEERS}
                help={<PeersHelpContent />}
                helpTitle={t('vsPeers.help.title')}
                moreActions={
                  multiple !== 'PB' && (
                    <OptionalTooltip
                      show={disableToggle}
                      title={t('vsPeers.noForecastData', {
                        metric: multiple,
                        baseMetricLabel: getBaseMetricLabel(
                          metricsData[multiple]?.label
                        ),
                      })}
                    >
                      <ToggleWrapper>
                        <Toggle
                          disabled={disableToggle}
                          name="forward-multiple-toggle-peers"
                          label={
                            <ToggleLabel>
                              {multiple === 'PE' && t('vsPeers.forwardPE')}
                              {multiple === 'PS' && t('vsPeers.forwardPS')}
                            </ToggleLabel>
                          }
                          value={toggleForward}
                          onChange={() => {
                            trackEvent({
                              subject: 'valuation',
                              action: `toggle_${!toggleForward ? 'on' : 'off'}`,
                              modifier: `vs_peers_forward_${multiple}`,
                            });
                            setToggleForward((toggleForward) => !toggleForward);
                          }}
                        />
                      </ToggleWrapper>
                    </OptionalTooltip>
                  )
                }
              />
            </MultipleVsPeersWrapper>
            {!isCardDismissed ? (
              <LearningTile contentKey={contentKey} onClose={handleDismiss} />
            ) : null}
          </>
        )}
        statements={statements}
      />
    </Track>
  );
};
export default MultipleVsPeers;
